/* @flow @format */

import { combineReducers, type CombinedReducer } from 'redux';

import type { Action } from '../types';
import { type ActionType, createAction } from '../utils';
import { fetchAgreementBegin } from './term';
import { fetchEntitiesBegin } from './entities';
import { fetchProgressBegin, uploadProgressBegin } from './progresses';
import { fetchRankingsBegin } from './ranking';
import { fetchUserConfigBegin } from './config';
import { finishActivityBegin } from './activity';
import { signinBegin } from './auth';

export type RetryAction =
  | ActionType<typeof fetchAgreementBegin>
  | ActionType<typeof signinBegin>
  | ActionType<typeof finishActivityBegin>
  | ActionType<typeof fetchEntitiesBegin>
  | ActionType<typeof fetchRankingsBegin>
  | ActionType<typeof fetchProgressBegin>
  | ActionType<typeof fetchUserConfigBegin>
  | ActionType<typeof uploadProgressBegin>;

export type ConnectionErrorProps = {|
  actions: RetryAction[],
|};

export type ErrorType =
  | {
      +type: 'CONNECTION_ERROR',
      +props: ConnectionErrorProps,
    }
  | {
      +type: 'UNRECOVERABLE_ERROR',
      +props: {},
    };

export type Meta = {
  // [`${Type}.${uid}`]: Timestamp
  +lastReloaded: number,
  +error: ?ErrorType,
};

// Actions

const SHOW_ERROR: 'proc/meta/SHOW_ERROR' = 'proc/meta/SHOW_ERROR';
const HIDE_ERROR: 'proc/meta/HIDE_ERROR' = 'proc/meta/HIDE_ERROR';
const RETRY_ACTION: 'proc/meta/RETRY_ACTION' = 'proc/meta/RETRY_ACTION';

type ShowErrorActionType = $Call<typeof showError, *>;
type HideErrorActionType = $Call<typeof hideError, *>;
type RetryActionActionType = $Call<typeof retryAction, *>;
export type MetaActionType = ShowErrorActionType | HideErrorActionType | RetryActionActionType;

// Reducer

const reducer: CombinedReducer<Meta, MetaActionType> = combineReducers({
  lastReloaded: (state = Date.now()) => state,
  error: (state: ?ErrorType = null, action: MetaActionType) => {
    switch (action.type) {
      case SHOW_ERROR: {
        const { payload } = action;
        if (payload.type === 'CONNECTION_ERROR' && state && state.type === 'CONNECTION_ERROR') {
          return { type: 'CONNECTION_ERROR', props: { actions: state.props.actions.concat(payload.props.actions) } };
        }
        return action.payload;
      }
      case HIDE_ERROR:
        return null;
      case RETRY_ACTION:
        return state;
      default:
        /*::(action: empty);*/
        return state;
    }
  },
});

export default reducer;

// Action creators

export const showError = (payload: ErrorType) => ({ type: SHOW_ERROR, payload });
export const showConnectionError = (action: RetryAction) => ({
  type: SHOW_ERROR,
  payload: {
    type: 'CONNECTION_ERROR',
    props: { actions: [action] },
  },
});
export const hideError = () => ({ type: HIDE_ERROR });

export const retryAction = createAction<typeof RETRY_ACTION, Action>(RETRY_ACTION);
